/**
 * BlocksRenderer + component registry (RFC T1+).
 *
 * Itera os blocks já validados (discriminated union) e renderiza cada um via um registry
 * `type -> componente`. Um `type` sem entrada no registry é ignorado (não deveria acontecer,
 * pois o Zod já descartou os desconhecidos, mas é a segunda linha de defesa da RFC §2.4).
 *
 * Adicionar um novo block = 1 entrada aqui + 1 membro no discriminated union do schema.
 * Nenhum block existente é tocado.
 */
import { ChartExpand } from "./chart-expand";
import { SuggestionCards } from "./suggestion-cards";
import { AddedListCard } from "./added-list-card";
import { KpiGroup } from "./kpi-group";
import { TableBlockView } from "./table-block";
import { FormBlockView, type FormBlockHandlers } from "./form-block";
import { AgentHub } from "./agent-hub";
import { ActionsBar } from "./actions-bar";
import { NoticeCard } from "./notice-card";
import { CardList } from "./card-list";
import { useEffect, useState } from "react";
import { useActionsTrack } from "@/_old/features/analytics";
import { useChatSlots, type ChatSlots } from "../slots";
import type { Suggestion } from "../types";
import {
  type Block,
  type ScheduleFormBlock,
  type ScheduleListBlock,
  type ChartBlock,
  type ChipsBlock,
  type ExpandableListBlock,
  type KpiGroupBlock,
  type TableBlock,
  type FormBlock,
  type AgentHubBlock,
  type ActionsBarBlock,
  type NoticeBlock,
  type CardListBlock,
  chartBlockToSpec,
} from "../schemas/blocks";

interface BlockRenderContext {
  /** Handler de clique reaproveitado pelos chips (mesmo do fluxo legado de sugestões). */
  onSuggestionClick?: (suggestion: Suggestion) => void;
  /** Handlers de submit/assist do block `form` (ST-5). */
  formHandlers?: FormBlockHandlers;
  /** Componentes de feature injetados (ver `../slots`). Ausente → block não renderiza. */
  slots?: ChatSlots;
}

/** Converte um item de chips block no `Suggestion` que o SuggestionCards consome. */
function chipToSuggestion(item: ChipsBlock["items"][number]): Suggestion {
  return {
    text: item.text,
    action: item.action ?? "",
    category: item.category ?? "",
  };
}

/**
 * Envelope do `schedule_form`: o "Cancelar" (e o encerramento após salvar) descarta o form
 * localmente, sem chamar a API e sem mutar o histórico de mensagens. Fica aqui — e não numa
 * prop encadeada por 4 níveis — porque é estado puramente local deste block.
 */
function ScheduleFormBlockView({
  block,
  Slot,
}: {
  block: ScheduleFormBlock;
  Slot: NonNullable<ChatSlots["schedule_form"]>;
}) {
  const [dismissed, setDismissed] = useState(false);
  const { onActionClick } = useActionsTrack();

  // O card abriu no chat. O `key` pelo id do block faz este componente remontar a cada
  // emissão, então o efeito roda uma vez por card e não a cada re-render.
  useEffect(() => {
    onActionClick("AGENT_ACTION_CARD_VIEW", "CHATBOT", {
      card_type: block.mode === "edit" ? "edit_schedule" : "new_schedule",
      mode: block.mode === "edit" ? "edit" : "new",
      surface: "ai_central",
      source: "lia",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (dismissed) return null;
  return (
    <Slot
      schedule={block.mode === "edit" ? block.schedule : null}
      onDone={() => setDismissed(true)}
    />
  );
}

const REGISTRY: Record<
  Block["type"],
  (block: Block, ctx: BlockRenderContext) => JSX.Element | null
> = {
  chart: (block) => (
    <ChartExpand spec={chartBlockToSpec(block as ChartBlock)} />
  ),
  chips: (block, ctx) =>
    ctx.onSuggestionClick ? (
      <SuggestionCards
        suggestions={(block as ChipsBlock).items.map(chipToSuggestion)}
        onSuggestionClick={ctx.onSuggestionClick}
      />
    ) : null,
  expandable_list: (block) => {
    const b = block as ExpandableListBlock;
    if (!b.items.length) return null;
    return <AddedListCard label={b.label ?? ""} emails={b.items} />;
  },
  kpi_group: (block) => <KpiGroup block={block as KpiGroupBlock} />,
  table: (block) => <TableBlockView block={block as TableBlock} />,
  form: (block, ctx) => (
    <FormBlockView block={block as FormBlock} handlers={ctx.formHandlers} />
  ),
  agent_hub: (block, ctx) => (
    <AgentHub
      block={block as AgentHubBlock}
      onSuggestionClick={ctx.onSuggestionClick}
    />
  ),
  actions_bar: (block, ctx) => (
    <ActionsBar
      block={block as ActionsBarBlock}
      onSuggestionClick={ctx.onSuggestionClick}
    />
  ),
  notice: (block) => <NoticeCard block={block as NoticeBlock} />,
  card_list: (block) => <CardList block={block as CardListBlock} />,
  schedule_form: (block, ctx) => {
    const b = block as ScheduleFormBlock;
    const Slot = ctx.slots?.schedule_form;
    if (!Slot) return null;
    // `key` pelo id do block: cada emissão traz um id novo (`rpt_sched_form_<hex>`), então
    // um form reemitido remonta e reinicializa os defaults do react-hook-form.
    return <ScheduleFormBlockView key={b.id} block={b} Slot={Slot} />;
  },
  schedule_list: (block, ctx) => {
    const b = block as ScheduleListBlock;
    const Slot = ctx.slots?.schedule_list;
    if (!Slot) return null;
    return <Slot schedules={b.schedules} />;
  },
};

export function BlocksRenderer({
  blocks,
  onSuggestionClick,
  formHandlers,
}: {
  blocks?: Block[] | null;
  onSuggestionClick?: (suggestion: Suggestion) => void;
  formHandlers?: FormBlockHandlers;
}) {
  const slots = useChatSlots();
  if (!blocks || blocks.length === 0) return null;
  const ctx: BlockRenderContext = { onSuggestionClick, formHandlers, slots };
  return (
    // min-w-0 em toda a cadeia (wrapper + cada block) permite que blocos largos (tabela)
    // rolem dentro da largura do chat em vez de esticar a mensagem inteira (overflow).
    <div className="flex min-w-0 flex-col gap-2 px-[1.7rem]">
      {blocks.map((block) => {
        const render = REGISTRY[block.type];
        if (!render) return null;
        return (
          <div key={block.id} className="min-w-0">
            {render(block, ctx)}
          </div>
        );
      })}
    </div>
  );
}
